import { useCallback, useEffect, useState } from 'react';
import { Finding, FindingListResponse } from '../types/finding';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

// Statuses that need user attention — mirrors FindingsListPage's 'actionable' filter.
const ACTIONABLE_STATUSES = ['open', 'needs_manual_review'];

export interface ApplicationFindingsFilters {
  severity?: string | null;
  /** Defaults to 'actionable' (open + needs_manual_review), matching the Findings list page. */
  status?: string | null;
  scanType?: string | null;
  /** Selected scan batches; the findings page allows several at once. */
  scanBatchIds?: string[];
}

interface UseApplicationFindingsResult {
  findings: Finding[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetch the findings for an application using the same query params and client-side
 * 'actionable' filtering as FindingsListPage, so consumers (e.g. the findings sidebar)
 * reproduce the exact same list and order the user saw on the Findings page.
 */
export function useApplicationFindings(
  applicationId?: string | null,
  filters: ApplicationFindingsFilters = {}
): UseApplicationFindingsResult {
  const api = useAuthenticatedApi();
  const [findings, setFindings] = useState<Finding[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { severity, scanType } = filters;
  const statusFilter = filters.status ?? 'actionable';
  // Callers rebuild the array each render, so the fetch keys off the joined ids.
  const scanBatchKey = (filters.scanBatchIds ?? []).join(',');

  const fetchFindings = useCallback(async () => {
    if (!applicationId) {
      setFindings([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('application_id', applicationId);
      if (severity) params.append('severity', severity);
      // For 'actionable'/'all', don't send status to backend — we filter client-side.
      if (statusFilter && statusFilter !== 'actionable' && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (scanType) params.append('scan_type', scanType);
      scanBatchKey.split(',').filter(Boolean).forEach((id) => params.append('scan_batch_id', id));
      params.append('limit', '500');

      const url = `${apiEndpoints.findings.list()}?${params.toString()}`;
      const response = await api.get<FindingListResponse>(url);

      const result =
        statusFilter === 'actionable'
          ? response.findings.filter((f) => ACTIONABLE_STATUSES.includes(f.status))
          : response.findings;
      setFindings(result);
    } catch (err: any) {
      console.error('Error fetching findings:', err);
      setError(err?.message || 'Failed to load findings');
      setFindings([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, applicationId, severity, statusFilter, scanType, scanBatchKey]);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  return { findings, isLoading, error };
}
